/**
 * Shared constants + pure helpers for the radar playback layer/control.
 * Kept in a non-component module so react-refresh stays happy (the `.tsx`
 * files may then export only components).
 */
import type { CommonTranslations } from '@/i18n/types';
import type { Language } from '@/i18n/types';

export const RAIN_RADAR_PANE = 'rainRadar';
export const RAIN_RADAR_PANE_Z_INDEX = 350;

type RadarPlaybackCopy = CommonTranslations['radarPlayback'];

/**
 * Map app language to standard BCP 47 locale tag for Intl APIs.
 * @param language - App language code ('fi', 'en', 'sv', 'es')
 * @returns BCP 47 locale string (e.g. 'fi-FI', 'en-US', 'sv-SE', 'es-ES')
 */
function languageToLocale(language: Language): string {
  const localeMap: Record<Language, string> = {
    fi: 'fi-FI',
    en: 'en-US',
    sv: 'sv-SE',
    es: 'es-ES',
  };
  return localeMap[language] || 'en-US';
}

export function formatTimestamp(
  value: string | null | undefined,
  copy: RadarPlaybackCopy,
  language?: Language,
): string {
  if (!value) return copy.noTimestamp;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  
  const locale = language ? languageToLocale(language) : undefined;
  
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
